import classes from './InferenceResults.module.scss';
import {
  CatalogTreeResponse,
  CentrifugeReport,
  CentrifugeRole,
  InferenceResult,
} from '@/models/models';
import GenerateCentrifugeReportModal from '@/components/CentrifugeReports/GenerateCentrifugeReportModal';
import { TableSkeletonRows } from '@/components/LoadingSkeleton/LoadingSkeleton';
import PageHeader from '@/components/PageHeader/PageHeader';
import {
  API_GET_INFERENCE_RESULTS_ENDPOINT,
  API_GET_INFERENCE_RESULT_HIGHLIGHTS_ENDPOINT,
  API_GET_CSV_REPORT_ENDPOINT,
  API_GET_CATALOG_TREE_ENDPOINT,
  API_RERUN_INFERENCE_RESULTS_ENDPOINT,
  createApiGetPdfReportEndpoint,
} from '@/api/config';

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  DatePickerInput,
  type DatesRangeValue,
  type DateStringValue,
} from '@mantine/dates';

import IconDownload from '@tabler/icons-react/IconDownload';
import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';
import IconArrowUp from '@tabler/icons-react/IconArrowUp';
import IconArrowDown from '@tabler/icons-react/IconArrowDown';
import IconTableExport from '@tabler/icons-react/IconTableExport';
import IconCalendar from '@tabler/icons-react/IconCalendar';
import IconColumns from '@tabler/icons-react/IconColumns';
import IconRefresh from '@tabler/icons-react/IconRefresh';
import IconSettings from '@tabler/icons-react/IconSettings';
import IconReportAnalytics from '@tabler/icons-react/IconReportAnalytics';

import apiClient from '@/api/client';
import { useLanguage } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/translations';
import {
  centrifugeRole,
  sampleSourceDisplayName,
} from '@/utils/sampleSources';
import {
  DATE_PICKER_VALUE_FORMAT,
  formatDisplayDateTime,
} from '@/utils/dateFormat';
import { useSampleSourceCatalog } from '@/hooks/useSampleSourceCatalog';
import { isOperatorUser } from '@/utils/auth';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SortDirection = 'asc' | 'desc';
type SortableColumn = keyof Pick<
  InferenceResult,
  | 'id'
  | 'uploaded_at'
  | 'reading_number'
  | 'date_time'
  | 'operator'
  | 'sample_source'
  | 'rig_number'
  | 'well_number'
  | 'instrument_serial_number'
  | 'qaqc_status'
>;

interface SortConfig {
  column: SortableColumn;
  direction: SortDirection;
}

interface RigWellOption {
  value: string;
  rigName: string;
  wellName: string;
  rigId: number;
  wellId: number;
}

interface AppliedFilters {
  rigName: string | null;
  wellName: string | null;
  sampleSources: string[];
  dateRange: DatesRangeValue;
  uploadedAtRange: DatesRangeValue;
}

interface InferenceResultsQueryParams {
  limit?: number;
  offset?: number;
  rig_name?: string;
  well_name?: string;
  sample_source?: string[];
  date_from?: string;
  date_to?: string;
  uploaded_from?: string;
  uploaded_to?: string;
  sort_by?: SortableColumn;
  sort_order?: SortDirection;
}

interface InferenceResultsResponse {
  data: InferenceResult[];
  total: number;
}

interface InferenceResultHighlightsResponse {
  data: Array<Pick<InferenceResult, 'date' | 'uploaded_at'>>;
}

interface RerunInferenceResultsResponse {
  rerun_count: number;
  skipped_ids: number[];
  not_found_ids: number[];
  failed_results: Array<{ source_id: number; message: string }>;
}

interface RerunFeedback {
  color: 'green' | 'yellow' | 'red';
  message: string;
}

type ExportMode = 'selected' | 'all';
type ConfigurableColumn =
  | 'id'
  | 'instrument_serial_number'
  | 'reading_number'
  | 'operator'
  | 'uploaded_at';

const DEFAULT_ITEMS_PER_PAGE = 20;
const ITEMS_PER_PAGE_OPTIONS = ['10', '20', '50', '100'];
const EMPTY_RANGE: DatesRangeValue = [null, null];
const BARITE_REQUIRED_CENTRIFUGE_ROLES: CentrifugeRole[] = [
  'ls_suction',
  'ls_effluent',
  'hs_effluent',
];
const BARITE_CENTRIFUGE_ROLES = new Set<CentrifugeRole>([
  ...BARITE_REQUIRED_CENTRIFUGE_ROLES,
  'drying_effluent',
]);
const DUAL_REQUIRED_CENTRIFUGE_ROLES: CentrifugeRole[] = [
  'centrifuge_1_suction',
  'centrifuge_1_effluent',
  'centrifuge_2_suction',
  'centrifuge_2_effluent',
];
const DUAL_CENTRIFUGE_ROLES = new Set<CentrifugeRole>(
  DUAL_REQUIRED_CENTRIFUGE_ROLES
);
const MAX_CENTRIFUGE_MEASUREMENTS = 4;
const CONFIGURABLE_COLUMNS: ConfigurableColumn[] = [
  'id',
  'instrument_serial_number',
  'reading_number',
  'operator',
  'uploaded_at',
];

function normalizeDate(value: Date | string): Date {
  return value instanceof Date ? value : parseISO(value);
}

function formatApiDate(value: Date | string | null | undefined): string | undefined {
  return value ? format(normalizeDate(value), 'yyyy-MM-dd') : undefined;
}

function formatApiDateTime(
  value: Date | string | null | undefined,
  boundary: 'start' | 'end'
): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = boundary === 'start'
    ? startOfDay(normalizeDate(value))
    : endOfDay(normalizeDate(value));

  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

function dateKeyFromValue(value: Date | string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  return typeof value === 'string'
    ? value.slice(0, 10)
    : format(value, 'yyyy-MM-dd');
}

function completeSingleDateRange(range: DatesRangeValue): DatesRangeValue {
  const [start, end] = range;

  if (start && !end) {
    return [start, start];
  }

  if (!start && end) {
    return [end, end];
  }

  return range;
}

function filtersToKey(filters: AppliedFilters): string {
  return JSON.stringify({
    rigName: filters.rigName,
    wellName: filters.wellName,
    sampleSources: filters.sampleSources,
    dateRange: filters.dateRange.map(dateKeyFromValue),
    uploadedAtRange: filters.uploadedAtRange.map(dateKeyFromValue),
  });
}

function formatRigWellLabel(rigName?: string | null, wellName?: string | null): string {
  const rig = (rigName || '').trim();
  const well = (wellName || '').trim();

  if (rig && well) {
    return `${rig} - ${well}`;
  }

  return rig || well;
}

function rigWellOptionsFromCatalog(catalog: CatalogTreeResponse): RigWellOption[] {
  return catalog.operators
    .flatMap((operator) => operator.rigs)
    .flatMap((rig) => rig.wells.map((well): RigWellOption => ({
      value: `${rig.name} - ${well.name}`,
      rigName: rig.name,
      wellName: well.name,
      rigId: rig.id,
      wellId: well.id,
    })))
    .sort((a, b) => a.value.localeCompare(b.value));
}

function getFileName(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) {
    return fallback;
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
  return fileNameMatch?.[1] || fallback;
}

function stringifyApiDetail(detail: unknown): string | null {
  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item !== 'object' || item === null || !('msg' in item)) {
          return null;
        }

        const message = (item as { msg?: unknown }).msg;
        return typeof message === 'string' ? message : null;
      })
      .filter((message): message is string => Boolean(message));

    return messages.length > 0 ? messages.join('; ') : null;
  }

  return null;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (
      error as {
        response?: {
          data?: string | { detail?: unknown; message?: unknown };
          status?: number;
          statusText?: string;
        };
      }
    ).response;
    const data = response?.data;

    if (typeof data === 'string' && data.trim()) {
      return `${fallback}: ${data}`;
    }

    if (data && typeof data === 'object') {
      const detail = stringifyApiDetail(data.detail);
      const message =
        typeof data.message === 'string' && data.message ? data.message : null;

      if (detail || message) {
        return `${fallback}: ${detail || message}`;
      }
    }

    if (response?.status) {
      return `${fallback}: ${response.status} ${response.statusText || ''}`.trim();
    }
  }

  return error instanceof Error && error.message
    ? `${fallback}: ${error.message}`
    : fallback;
}

function withDisplayDateTime(items: InferenceResult[]): InferenceResult[] {
  return items.map((item) => {
    return {
      ...item,
      date_time: `${item.date}T${item.time}`,
    };
  });
}

function formatQaqcStatus(
  status: string | null | undefined,
  t: (key: TranslationKey) => string
): string {
  const normalizedStatus = String(status || '').trim().toUpperCase();

  if (!normalizedStatus) {
    return '-';
  }

  if (normalizedStatus === 'OK' || normalizedStatus === 'PASS') {
    return t('results.status.pass');
  }

  if (normalizedStatus === 'WARNING') {
    return t('results.status.warning');
  }

  if (normalizedStatus === 'RERUN' || normalizedStatus === 'FAIL') {
    return t('results.status.fail');
  }

  return normalizedStatus
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getQaqcStatusClass(status?: string | null): string {
  const normalizedStatus = String(status || '').trim().toUpperCase();

  if (normalizedStatus === 'OK' || normalizedStatus === 'PASS') {
    return classes.qaqcStatusPass;
  }

  if (normalizedStatus === 'WARNING') {
    return classes.qaqcStatusWarning;
  }

  if (normalizedStatus === 'RERUN' || normalizedStatus === 'FAIL') {
    return classes.qaqcStatusFail;
  }

  return classes.qaqcStatusInfo;
}

function isRerunnableQaqcStatus(status?: string | null): boolean {
  return ['WARNING', 'RERUN', 'FAIL'].includes(
    String(status || '').trim().toUpperCase()
  );
}

export default function ReportTable() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const operatorUser = isOperatorUser();
  const [searchParams] = useSearchParams();
  const initialWellName = searchParams.get('well_name');
  const initialRigName = searchParams.get('rig_name');
  const initialSampleSourceNames = searchParams
    .getAll('sample_source_name')
    .filter(Boolean);
  const initialDateRange: DatesRangeValue = [
    dateKeyFromValue(searchParams.get('date_from')),
    dateKeyFromValue(searchParams.get('date_to')),
  ];
  const initialUploadedAtRange: DatesRangeValue = [
    dateKeyFromValue(searchParams.get('uploaded_from')),
    dateKeyFromValue(searchParams.get('uploaded_to')),
  ];

  const [data, setData] = useState<InferenceResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'id',
    direction: 'desc',
  });

  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [selectedRowsById, setSelectedRowsById] = useState<Map<number, InferenceResult>>(
    new Map()
  );
  const selectedRowIdSet = useMemo(
    () => new Set(selectedRowIds),
    [selectedRowIds]
  );

  const [selectedRigWell, setSelectedRigWell] = useState<string | null>(
    formatRigWellLabel(initialRigName, initialWellName) || null
  );
  const [selectedSampleSources, setSelectedSampleSources] = useState<string[]>(
    initialSampleSourceNames
  );
  const [dateRange, setDateRange] = useState<DatesRangeValue>(initialDateRange);
  const [uploadedAtRange, setUploadedAtRange] =
    useState<DatesRangeValue>(initialUploadedAtRange);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    rigName: initialRigName,
    wellName: initialWellName,
    sampleSources: initialSampleSourceNames,
    dateRange: initialDateRange,
    uploadedAtRange: initialUploadedAtRange,
  });

  const [rigWellOptions, setRigWellOptions] = useState<RigWellOption[]>([]);
  const [rigWellLoading, setRigWellLoading] = useState(false);
  const {
    sampleSources: sampleSourceCatalog,
    loading: sampleSourcesLoading,
  } = useSampleSourceCatalog();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState<ExportMode | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Set<ConfigurableColumn>>(
    new Set(['reading_number', 'operator'])
  );
  const [rerunningIds, setRerunningIds] = useState<number[]>([]);
  const [rerunFeedback, setRerunFeedback] = useState<RerunFeedback | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [centrifugeModalOpened, setCentrifugeModalOpened] = useState(false);
  const [reuseConfirmationOpened, setReuseConfirmationOpened] = useState(false);
  const rerunningIdSet = useMemo(() => new Set(rerunningIds), [rerunningIds]);

  const [reportDateKeys, setReportDateKeys] = useState<Set<string>>(new Set());
  const [uploadedDateKeys, setUploadedDateKeys] = useState<Set<string>>(
    new Set()
  );
  const [dateHighlightsLoading, setDateHighlightsLoading] = useState(false);
  const highlightFilterKey = useMemo(
    () => filtersToKey(appliedFilters),
    [appliedFilters]
  );
  const lastHighlightFilterKey = useRef<string | null>(null);
  const highlightRequestId = useRef(0);

  const rigWellOptionValues = useMemo(
    () => rigWellOptions.map((option) => option.value),
    [rigWellOptions]
  );

  const hasActiveFilters = Boolean(
    selectedRigWell ||
      selectedSampleSources.length > 0 ||
      dateRange[0] ||
      dateRange[1] ||
      uploadedAtRange[0] ||
      uploadedAtRange[1]
  );

  const currentPageIds = useMemo(() => data.map((item) => item.id), [data]);
  const selectedOnPageCount = currentPageIds.filter((id) =>
    selectedRowIdSet.has(id)
  ).length;
  const allPageRowsSelected =
    currentPageIds.length > 0 && selectedOnPageCount === currentPageIds.length;
  const somePageRowsSelected =
    selectedOnPageCount > 0 && selectedOnPageCount < currentPageIds.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const visibleColumnCount = 8 + visibleColumns.size;
  const centrifugeSelection = useMemo(() => {
    const selectedRows = selectedRowIds
      .map((id) => selectedRowsById.get(id))
      .filter((item): item is InferenceResult => Boolean(item));
    const selection: Partial<Record<CentrifugeRole, InferenceResult>> = {};
    let hasNonCentrifugeMeasurement = false;
    let hasDuplicateRole = false;
    let hasBariteRole = false;
    let hasDualRole = false;
    const rigWellKeys = new Set(
      selectedRows.map((item) => `${item.rig_number}\n${item.well_number}`)
    );

    for (const item of selectedRows) {
      const role = centrifugeRole(sampleSourceCatalog, item.sample_source);
      if (!role) {
        hasNonCentrifugeMeasurement = true;
        continue;
      }
      hasBariteRole ||= BARITE_CENTRIFUGE_ROLES.has(role);
      hasDualRole ||= DUAL_CENTRIFUGE_ROLES.has(role);
      if (selection[role]) {
        hasDuplicateRole = true;
        continue;
      }
      selection[role] = item;
    }

    const mixedModes = hasBariteRole && hasDualRole;
    const mode = hasDualRole && !hasBariteRole ? 'dual_polishing' : 'barite_recovery';
    const requiredRoles = mode === 'dual_polishing'
      ? DUAL_REQUIRED_CENTRIFUGE_ROLES
      : BARITE_REQUIRED_CENTRIFUGE_ROLES;
    const missingRoles = requiredRoles.filter((role) => !selection[role]);
    const requiredCount = requiredRoles.length - missingRoles.length;
    const missingRoleLabels = missingRoles.map((role) =>
      t(`centrifuge.role.${role}` as TranslationKey)
    );
    const linkedReportIds = Array.from(new Set(
      selectedRows
        .map((item) => item.centrifuge_report_id)
        .filter((reportId): reportId is string => Boolean(reportId))
    ));
    let valid = false;
    let reason = '';

    if (selectedRows.length !== selectedRowIds.length) {
      reason = t('centrifuge.selectionUnavailable');
    } else if (selectedRows.length > MAX_CENTRIFUGE_MEASUREMENTS) {
      reason = t('centrifuge.selectionTooMany', {
        count: selectedRows.length,
        max: MAX_CENTRIFUGE_MEASUREMENTS,
      });
    } else if (mixedModes) {
      reason = t('centrifuge.selectionMixedModes');
    } else if (hasNonCentrifugeMeasurement || hasDuplicateRole) {
      reason = t('centrifuge.selectionRoles');
    } else if (rigWellKeys.size !== 1) {
      reason = t('centrifuge.selectionWell');
    } else if (missingRoles.length > 0) {
      reason = t('centrifuge.selectionMissing', {
        roles: missingRoleLabels.join(', '),
      });
    } else if (
      mode === 'barite_recovery' &&
      selectedRows.length !== 3 && selectedRows.length !== 4
    ) {
      reason = t('centrifuge.selectionCount');
    } else if (
      mode === 'barite_recovery' &&
      selectedRows.length === 4 && !selection.drying_effluent
    ) {
      reason = t('centrifuge.selectionRoles');
    } else if (mode === 'dual_polishing' && selectedRows.length !== 4) {
      reason = t('centrifuge.selectionDualRoles');
    } else {
      valid = true;
    }

    const highlightMissingRows = (
      selectedRows.length > 0 &&
      selectedRows.length <= requiredRoles.length + (mode === 'barite_recovery' ? 1 : 0) &&
      selectedRows.length === selectedRowIds.length &&
      !hasNonCentrifugeMeasurement &&
      !hasDuplicateRole &&
      rigWellKeys.size === 1 &&
      missingRoles.length > 0
    );

    return {
      valid,
      reason,
      selection,
      requiredCount,
      requiredTotal: requiredRoles.length,
      mode,
      missingRoles,
      linkedReportIds,
      highlightMissingRows,
      rigWellKey: highlightMissingRows ? [...rigWellKeys][0] : null,
    };
  }, [sampleSourceCatalog, selectedRowIds, selectedRowsById, t]);

  const shouldHighlightCentrifugeRow = (item: InferenceResult): boolean => {
    if (
      !centrifugeSelection.highlightMissingRows ||
      selectedRowIdSet.has(item.id) ||
      `${item.rig_number}\n${item.well_number}` !== centrifugeSelection.rigWellKey
    ) {
      return false;
    }

    const role = centrifugeRole(sampleSourceCatalog, item.sample_source);
    return Boolean(role && centrifugeSelection.missingRoles.includes(role));
  };

  const clearSelection = useCallback(() => {
    setSelectedRowIds([]);
    setSelectedRowsById(new Map());
    setReuseConfirmationOpened(false);
  }, []);

  const applyFilterPatch = useCallback(
    (patch: Partial<AppliedFilters>) => {
      const nextFilters = {
        ...appliedFilters,
        ...patch,
      };

      if (filtersToKey(nextFilters) === highlightFilterKey) {
        return;
      }

      setCurrentPage(1);
      setAppliedFilters(nextFilters);
    },
    [appliedFilters, highlightFilterKey]
  );

  const buildFilterParams = useCallback((): InferenceResultsQueryParams => {
    const params: InferenceResultsQueryParams = {};
    if (appliedFilters.rigName) {
      params.rig_name = appliedFilters.rigName;
    }
    if (appliedFilters.wellName) {
      params.well_name = appliedFilters.wellName;
    }
    if (appliedFilters.sampleSources.length > 0) {
      params.sample_source = appliedFilters.sampleSources;
    }

    const completeDateRange = completeSingleDateRange(appliedFilters.dateRange);
    const completeUploadedAtRange = completeSingleDateRange(
      appliedFilters.uploadedAtRange
    );
    const dateFrom = formatApiDate(completeDateRange[0]);
    const dateTo = formatApiDate(completeDateRange[1]);
    const uploadedFrom = formatApiDateTime(
      completeUploadedAtRange[0],
      'start'
    );
    const uploadedTo = formatApiDateTime(completeUploadedAtRange[1], 'end');

    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }
    if (uploadedFrom) {
      params.uploaded_from = uploadedFrom;
    }
    if (uploadedTo) {
      params.uploaded_to = uploadedTo;
    }

    return params;
  }, [appliedFilters]);

  const buildResultParams = useCallback(
    (
      overrides: Partial<Pick<InferenceResultsQueryParams, 'limit' | 'offset'>> = {}
    ): InferenceResultsQueryParams => ({
      ...buildFilterParams(),
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
      sort_by: sortConfig.column,
      sort_order: sortConfig.direction,
      ...overrides,
    }),
    [buildFilterParams, currentPage, itemsPerPage, sortConfig]
  );

  const fetchInferenceResults = useCallback(
    async (
      overrides: Partial<Pick<InferenceResultsQueryParams, 'limit' | 'offset'>> = {},
      signal?: AbortSignal
    ): Promise<InferenceResultsResponse> => {
      const response = await apiClient.get<InferenceResultsResponse>(
        API_GET_INFERENCE_RESULTS_ENDPOINT,
        {
          params: buildResultParams(overrides),
          paramsSerializer: { indexes: null },
          headers: {
            Accept: 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
          signal,
        }
      );

      return response.data;
    },
    [buildResultParams]
  );

  const fetchDateHighlights = useCallback(
    async (count: number, requestId: number) => {
      if (count === 0) {
        if (highlightRequestId.current === requestId) {
          setReportDateKeys(new Set());
          setUploadedDateKeys(new Set());
          setDateHighlightsLoading(false);
        }
        return;
      }

      try {
        const response = await apiClient.get<InferenceResultHighlightsResponse>(
          API_GET_INFERENCE_RESULT_HIGHLIGHTS_ENDPOINT,
          {
            params: buildFilterParams(),
            paramsSerializer: { indexes: null },
          }
        );
        const nextReportDateKeys = new Set<string>();
        const nextUploadedDateKeys = new Set<string>();

        response.data.data.forEach((item) => {
          const reportDate = dateKeyFromValue(item.date);
          const uploadedDate = dateKeyFromValue(item.uploaded_at);

          if (reportDate) {
            nextReportDateKeys.add(reportDate);
          }
          if (uploadedDate) {
            nextUploadedDateKeys.add(uploadedDate);
          }
        });

        if (highlightRequestId.current === requestId) {
          setReportDateKeys(nextReportDateKeys);
          setUploadedDateKeys(nextUploadedDateKeys);
        }
      } catch (error) {
        console.error('Error fetching report dates:', error);
      } finally {
        if (highlightRequestId.current === requestId) {
          setDateHighlightsLoading(false);
        }
      }
    },
    [buildFilterParams]
  );

  const downloadCsv = useCallback(async (idsToExport: number[]) => {
    if (idsToExport.length === 0) {
      return;
    }

    const response = await apiClient({
      url: API_GET_CSV_REPORT_ENDPOINT,
      method: 'GET',
      params: {
        ir_ids: JSON.stringify(idsToExport),
      },
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: response.data.type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'];

    link.href = url;
    link.setAttribute(
      'download',
      getFileName(contentDisposition, t('results.csvFileName'))
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [t]);

  const fetchRigWellOptions = useCallback(async () => {
    setRigWellLoading(true);

    try {
      const response = await apiClient.get<CatalogTreeResponse>(
        API_GET_CATALOG_TREE_ENDPOINT
      );
      setRigWellOptions(rigWellOptionsFromCatalog(response.data));
    } catch (error) {
      console.error('Error fetching rig-well options:', error);
    } finally {
      setRigWellLoading(false);
    }
  }, []);

  const sampleSourceOptions = useMemo(
    () => sampleSourceCatalog
      .map((source) => ({
        value: source.english_name,
        label: language === 'es' ? source.spanish_name : source.english_name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [language, sampleSourceCatalog]
  );
  const selectedSampleSourceLabels = useMemo(() => {
    const labelsByValue = new Map(
      sampleSourceOptions.map((option) => [option.value, option.label])
    );

    return selectedSampleSources.map(
      (source) => labelsByValue.get(source) ?? source
    );
  }, [sampleSourceOptions, selectedSampleSources]);
  const collapseSampleSourceValues = selectedSampleSources.length > 1;

  useEffect(() => {
    fetchRigWellOptions();
  }, [fetchRigWellOptions]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const fetchTableData = async () => {
      const shouldRefreshHighlights =
        lastHighlightFilterKey.current !== highlightFilterKey;
      const nextHighlightRequestId = shouldRefreshHighlights
        ? highlightRequestId.current + 1
        : null;

      if (nextHighlightRequestId !== null) {
        highlightRequestId.current = nextHighlightRequestId;
        setDateHighlightsLoading(true);
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetchInferenceResults({}, controller.signal);

        if (!active) {
          return;
        }

        setData(withDisplayDateTime(response.data));
        setTotalCount(response.total);

        if (nextHighlightRequestId !== null) {
          lastHighlightFilterKey.current = highlightFilterKey;
          fetchDateHighlights(response.total, nextHighlightRequestId);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        console.error('Error fetching data:', error);
        setError(getApiErrorMessage(error, t('results.loadError')));

        if (
          nextHighlightRequestId !== null &&
          highlightRequestId.current === nextHighlightRequestId
        ) {
          setDateHighlightsLoading(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchTableData();

    return () => {
      active = false;
      controller.abort();
    };
  }, [fetchDateHighlights, fetchInferenceResults, highlightFilterKey, refreshVersion, t]);

  const handleSort = (column: SortableColumn) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        column,
        direction: 'asc',
      };
    });
  };

  const clearFilters = () => {
    const emptyFilters: AppliedFilters = {
      rigName: null,
      wellName: null,
      sampleSources: [],
      dateRange: EMPTY_RANGE,
      uploadedAtRange: EMPTY_RANGE,
    };

    setSelectedRigWell(null);
    setSelectedSampleSources([]);
    setDateRange(EMPTY_RANGE);
    setUploadedAtRange(EMPTY_RANGE);
    setCurrentPage(1);
    setAppliedFilters(emptyFilters);
  };

  const handleRigWellChange = useCallback(
    (value: string | null) => {
      setSelectedRigWell(value);

      const selectedOption = value
        ? rigWellOptions.find((option) => option.value === value)
        : null;

      applyFilterPatch({
        rigName: selectedOption?.rigName || null,
        wellName: selectedOption?.wellName || null,
      });
    },
    [applyFilterPatch, rigWellOptions]
  );

  const handleSampleSourceChange = useCallback(
    (value: string[]) => {
      setSelectedSampleSources(value);
      applyFilterPatch({ sampleSources: value });
    },
    [applyFilterPatch]
  );

  const handleDateRangeChange = useCallback(
    (value: DatesRangeValue) => {
      setDateRange(value);
      applyFilterPatch({ dateRange: value });
    },
    [applyFilterPatch]
  );

  const handleUploadedAtRangeChange = useCallback(
    (value: DatesRangeValue) => {
      setUploadedAtRange(value);
      applyFilterPatch({ uploadedAtRange: value });
    },
    [applyFilterPatch]
  );

  const toggleRowSelection = useCallback((item: InferenceResult) => {
    const selected = selectedRowIdSet.has(item.id);
    setSelectedRowIds((prev) =>
      selected
        ? prev.filter((rowId) => rowId !== item.id)
        : [...prev, item.id]
    );
    setSelectedRowsById((previous) => {
      const next = new Map(previous);
      if (selected) {
        next.delete(item.id);
      } else {
        next.set(item.id, item);
      }
      return next;
    });
  }, [selectedRowIdSet]);

  const toggleAllRowsOnPage = useCallback(() => {
    const removePage = currentPageIds.every((id) => selectedRowIdSet.has(id));
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      currentPageIds.forEach((id) => removePage ? next.delete(id) : next.add(id));
      return Array.from(next);
    });
    setSelectedRowsById((previous) => {
      const next = new Map(previous);
      data.forEach((item) => removePage ? next.delete(item.id) : next.set(item.id, item));
      return next;
    });
  }, [currentPageIds, data, selectedRowIdSet]);

  const rerunResults = useCallback(
    async (resultIds: number[]) => {
      const uniqueIds = Array.from(new Set(resultIds));
      if (uniqueIds.length === 0 || rerunningIds.length > 0) {
        return;
      }

      setRerunningIds(uniqueIds);
      setRerunFeedback(null);

      try {
        const response = await apiClient.post<RerunInferenceResultsResponse>(
          API_RERUN_INFERENCE_RESULTS_ENDPOINT,
          { inference_result_ids: uniqueIds }
        );
        const {
          rerun_count: rerunCount,
          skipped_ids: skippedIds,
          not_found_ids: notFoundIds,
          failed_results: failedResults,
        } = response.data;
        const skippedCount = skippedIds.length + notFoundIds.length;
        const messageParts: string[] = [];

        if (rerunCount > 0) {
          messageParts.push(t('results.rerunComplete', { count: rerunCount }));
        }
        if (skippedCount > 0) {
          messageParts.push(t('results.rerunSkipped', { count: skippedCount }));
        }
        if (failedResults.length > 0) {
          messageParts.push(
            t('results.rerunFailedCount', { count: failedResults.length })
          );
        }
        if (messageParts.length === 0) {
          messageParts.push(t('results.noRerunnableSelected'));
        }

        setRerunFeedback({
          color:
            failedResults.length > 0 || skippedCount > 0
              ? 'yellow'
              : rerunCount > 0
                ? 'green'
                : 'yellow',
          message: messageParts.join(' '),
        });

        if (rerunCount > 0) {
          setSelectedRowIds((previousIds) =>
            previousIds.filter((id) => !uniqueIds.includes(id))
          );
          setSelectedRowsById((previous) => {
            const next = new Map(previous);
            uniqueIds.forEach((id) => next.delete(id));
            return next;
          });
          setRefreshVersion((version) => version + 1);
        }
      } catch (error) {
        console.error('Rerun failed:', error);
        setRerunFeedback({
          color: 'red',
          message: getApiErrorMessage(error, t('results.rerunError')),
        });
      } finally {
        setRerunningIds([]);
      }
    },
    [rerunningIds.length, t]
  );

  const exportSelected = useCallback(async () => {
    if (selectedRowIds.length === 0) {
      return;
    }

    setExporting('selected');

    try {
      await downloadCsv(selectedRowIds);
    } catch (error) {
      console.error('Download failed:', error);
      setError(getApiErrorMessage(error, t('results.downloadError')));
    } finally {
      setExporting(null);
    }
  }, [downloadCsv, selectedRowIds, t]);

  const exportAllFiltered = useCallback(async () => {
    if (totalCount === 0) {
      return;
    }

    setExporting('all');

    try {
      const response = await fetchInferenceResults({
        limit: totalCount,
        offset: 0,
      });
      await downloadCsv(response.data.map((item) => item.id));
    } catch (error) {
      console.error('Download failed:', error);
      setError(getApiErrorMessage(error, t('results.downloadError')));
    } finally {
      setExporting(null);
    }
  }, [downloadCsv, fetchInferenceResults, totalCount, t]);

  const downloadPdfReport = useCallback(async (id: number) => {
    try {
      const response = await apiClient({
        url: createApiGetPdfReportEndpoint(id),
        method: 'GET',
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'];

      link.href = url;
      link.setAttribute(
        'download',
        getFileName(
          contentDisposition,
          t('results.pdfFileName', { id })
        )
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      setError(getApiErrorMessage(error, t('results.pdfDownloadError')));
    }
  }, [t]);

  const navigateToCalibration = useCallback(
    async (wellNumber: string, rigNumber: string) => {
      try {
        const exactOption = rigWellOptions.find(
          (option) => option.wellName === wellNumber && option.rigName === rigNumber
        );

        if (exactOption) {
          navigate(`/calibration/${exactOption.rigId}?wellId=${exactOption.wellId}`);
          return;
        }

        const response = await apiClient.get<CatalogTreeResponse>(
          API_GET_CATALOG_TREE_ENDPOINT
        );
        const fallbackOptions = rigWellOptionsFromCatalog(response.data);
        const fallbackOption = fallbackOptions.find(
          (option) => option.wellName === wellNumber && option.rigName === rigNumber
        );

        if (!fallbackOption) {
          const rigExists = fallbackOptions.some(
            (option) => option.rigName === rigNumber
          );
          setError(
            rigExists
              ? t('results.wellNotFound', { well: wellNumber, rig: rigNumber })
              : t('results.rigNotFound', { rig: rigNumber })
          );
          return;
        }
        navigate(
          `/calibration/${fallbackOption.rigId}?wellId=${fallbackOption.wellId}`
        );
      } catch (error) {
        console.error('Error fetching well:', error);
        setError(getApiErrorMessage(error, t('results.wellLookupError')));
      }
    },
    [navigate, rigWellOptions, t]
  );

  const SortIcon = ({ column }: { column: SortableColumn }) => {
    if (sortConfig.column !== column) {
      return null;
    }

    return sortConfig.direction === 'asc' ? (
      <IconArrowUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconArrowDown size={14} style={{ marginLeft: 4 }} />
    );
  };

  const toggleConfigurableColumn = (column: ConfigurableColumn) => {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  };

  const configurableColumnLabel = (column: ConfigurableColumn): string => {
    const labels: Record<ConfigurableColumn, string> = {
      id: t('results.resultId'),
      instrument_serial_number: t('results.instrumentSerial'),
      reading_number: t('results.readingNumber'),
      operator: t('results.operator'),
      uploaded_at: t('results.uploadedAt'),
    };
    return labels[column];
  };

  const sortableHeader = (column: SortableColumn, label: string) => (
    <Table.Th
      onClick={() => handleSort(column)}
      className={classes.sortedHeader}
      aria-sort={
        sortConfig.column === column
          ? sortConfig.direction === 'asc' ? 'ascending' : 'descending'
          : 'none'
      }
    >
      <span className={classes.sortableLabel}>
        {label}
        <SortIcon column={column} />
      </span>
    </Table.Th>
  );

  const getReportDayProps = useCallback(
    (date: DateStringValue) => ({
      className: dateHighlightsLoading
        ? classes.reportDayLoading
        : reportDateKeys.has(date)
          ? classes.reportDay
          : undefined,
      title: dateHighlightsLoading
        ? t('results.loadingDates')
        : reportDateKeys.has(date)
          ? t('results.reportsAvailable')
          : undefined,
    }),
    [dateHighlightsLoading, reportDateKeys, t]
  );

  const getUploadedDayProps = useCallback(
    (date: DateStringValue) => ({
      className: dateHighlightsLoading
        ? classes.reportDayLoading
        : uploadedDateKeys.has(date)
          ? classes.reportDay
          : undefined,
      title: dateHighlightsLoading
        ? t('results.loadingDates')
        : uploadedDateKeys.has(date)
          ? t('results.reportsUploaded')
          : undefined,
    }),
    [dateHighlightsLoading, uploadedDateKeys, t]
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title={t('common.error')} color="red">
        {error}
      </Alert>
    );
  }

  return (
    <div className={classes.resultsSurface}>
      <PageHeader
        title={t('results.title')}
        meta={t('results.count', { count: totalCount })}
        actions={(
          <Group gap="xs" wrap="wrap">
            <Menu closeOnItemClick={false} position="bottom-end" shadow="md">
              <Menu.Target>
                <Button
                  variant="default"
                  size="sm"
                  leftSection={<IconColumns size={17} />}
                  aria-label={t('results.columnsDescription')}
                >
                  {t('results.columns')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{t('results.columnsDescription')}</Menu.Label>
                {CONFIGURABLE_COLUMNS.map((column) => (
                  <Menu.Item
                    key={column}
                    role="menuitemcheckbox"
                    aria-checked={visibleColumns.has(column)}
                    leftSection={(
                      <Checkbox
                        checked={visibleColumns.has(column)}
                        readOnly
                        tabIndex={-1}
                        aria-hidden="true"
                        size="xs"
                      />
                    )}
                    onClick={() => toggleConfigurableColumn(column)}
                  >
                    {configurableColumnLabel(column)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <Button
              variant="light"
              color="green"
              size="sm"
              leftSection={<IconTableExport size={17} />}
              onClick={exportAllFiltered}
              disabled={loading || totalCount === 0}
              loading={exporting === 'all'}
            >
              {t('results.exportFilteredAction')}
            </Button>
          </Group>
        )}
      />

      {rerunFeedback && (
        <Alert
          color={rerunFeedback.color}
          icon={<IconRefresh size="1rem" />}
          withCloseButton
          onClose={() => setRerunFeedback(null)}
          className={classes.rerunFeedback}
        >
          {rerunFeedback.message}
        </Alert>
      )}

      <div className={classes.toolbar}>
        <div className={classes.filterControls}>
          <Select
            label={t('nav.rigWell')}
            placeholder={t('results.allRigWells')}
            data={rigWellOptionValues}
            value={selectedRigWell}
            onChange={handleRigWellChange}
            rightSection={rigWellLoading ? <Loader size="xs" /> : undefined}
            disabled={rigWellLoading}
            searchable
            clearable
            limit={100}
            nothingFoundMessage={t('results.noRigWellMatches')}
            comboboxProps={{ withinPortal: false }}
          />

          <DatePickerInput
            type="range"
            label={t('results.measurementTakenAt')}
            placeholder={t('results.measurementPlaceholder')}
            value={dateRange}
            valueFormat={DATE_PICKER_VALUE_FORMAT}
            onChange={handleDateRangeChange}
            allowSingleDateInRange
            clearable
            leftSection={<IconCalendar size="16" />}
            leftSectionPointerEvents="none"
            maxDate={new Date()}
            size="sm"
            getDayProps={getReportDayProps}
            classNames={{
              calendarHeader: classes.calendarHeader,
              calendarHeaderControl: classes.calendarHeaderControl,
              calendarHeaderLevel: classes.calendarHeaderLevel,
            }}
          />

          <MultiSelect
            label={t('results.sampleSource')}
            placeholder={
              collapseSampleSourceValues
                ? t('results.sampleSourcesSelected', {
                    count: selectedSampleSources.length,
                  })
                : t('results.sampleSourcePlaceholder')
            }
            data={sampleSourceOptions}
            value={selectedSampleSources}
            onChange={handleSampleSourceChange}
            rightSection={sampleSourcesLoading ? <Loader size="xs" /> : undefined}
            title={
              collapseSampleSourceValues
                ? selectedSampleSourceLabels.join(', ')
                : undefined
            }
            classNames={{
              pill: collapseSampleSourceValues
                ? classes.collapsedSampleSourcePill
                : undefined,
              inputField: collapseSampleSourceValues
                ? classes.collapsedSampleSourceInput
                : undefined,
            }}
            searchable
            clearable
            nothingFoundMessage={t('results.noSampleSourceMatches')}
            comboboxProps={{ withinPortal: false }}
          />

          <DatePickerInput
            type="range"
            label={t('results.uploadedAt')}
            placeholder={t('results.uploadedPlaceholder')}
            value={uploadedAtRange}
            valueFormat={DATE_PICKER_VALUE_FORMAT}
            onChange={handleUploadedAtRangeChange}
            allowSingleDateInRange
            clearable
            leftSection={<IconCalendar size="16" />}
            leftSectionPointerEvents="none"
            maxDate={new Date()}
            size="sm"
            getDayProps={getUploadedDayProps}
            classNames={{
              calendarHeader: classes.calendarHeader,
              calendarHeaderControl: classes.calendarHeaderControl,
              calendarHeaderLevel: classes.calendarHeaderLevel,
            }}
          />

          <Button
            variant="subtle"
            color="green"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className={classes.clearFiltersButton}
            h={36}
          >
            {t('results.clearFilters')}
          </Button>
        </div>
      </div>

      {selectedRowIds.length > 0 && (
        <div className={classes.selectionToolbar}>
          <div className={classes.selectionInfo}>
            <Text size="sm" fw={700}>
              {t('results.selectedAcrossPages', { count: selectedRowIds.length })}
            </Text>
            {(!centrifugeSelection.valid || centrifugeSelection.linkedReportIds.length > 0) && (
              <Text size="xs" className={classes.selectionReason} role="status">
                {centrifugeSelection.valid
                  ? t('centrifuge.selectionReuse', {
                      reports: centrifugeSelection.linkedReportIds.join(', '),
                    })
                  : centrifugeSelection.reason}
              </Text>
            )}
          </div>
          <Group gap="xs" wrap="wrap" className={classes.selectionButtons}>
            <Button
              variant="light"
              color="green"
              leftSection={<IconTableExport size={17} />}
              onClick={exportSelected}
              disabled={loading}
              loading={exporting === 'selected'}
              size="sm"
            >
              {t('results.exportSelected', { count: selectedRowIds.length })}
            </Button>
            {!operatorUser && (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconRefresh size={17} />}
                onClick={() => rerunResults(selectedRowIds)}
                loading={rerunningIds.length > 0}
                disabled={loading}
                size="sm"
              >
                {t('results.rerunSelected', { count: selectedRowIds.length })}
              </Button>
            )}
            <Tooltip
              label={
                centrifugeSelection.valid
                  ? centrifugeSelection.linkedReportIds.length > 0
                    ? t('centrifuge.selectionReuse', {
                        reports: centrifugeSelection.linkedReportIds.join(', '),
                      })
                    : t('centrifuge.generate')
                  : centrifugeSelection.reason
              }
              withArrow
            >
              <span>
                <Button
                  color="green"
                  leftSection={<IconReportAnalytics size={17} />}
                  onClick={() => {
                    if (centrifugeSelection.linkedReportIds.length > 0) {
                      setReuseConfirmationOpened(true);
                    } else {
                      setCentrifugeModalOpened(true);
                    }
                  }}
                  disabled={!centrifugeSelection.valid || loading}
                  size="sm"
                >
                  {t('centrifuge.generateProgress', {
                    count: centrifugeSelection.requiredCount,
                    total: centrifugeSelection.requiredTotal,
                  })}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="subtle"
              color="green"
              onClick={clearSelection}
              disabled={rerunningIds.length > 0}
              size="sm"
            >
              {t('results.clearSelection')}
            </Button>
          </Group>
        </div>
      )}

      <ScrollArea className={classes.tableContainer}>
        <Table
          className={classes.resultsTable}
          striped
          highlightOnHover
          withTableBorder
          stickyHeader
          stickyHeaderOffset={-1}
          aria-busy={loading}
          style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.selectionColumn}>
                <Checkbox
                  onChange={toggleAllRowsOnPage}
                  checked={allPageRowsSelected}
                  indeterminate={somePageRowsSelected}
                  aria-label={t('results.selectPage')}
                />
              </Table.Th>
              {sortableHeader('date_time', t('results.measurementTakenAt'))}
              {visibleColumns.has('id') &&
                sortableHeader('id', t('results.resultId'))}
              {visibleColumns.has('instrument_serial_number') &&
                sortableHeader('instrument_serial_number', t('results.instrumentSerial'))}
              {visibleColumns.has('reading_number') &&
                sortableHeader('reading_number', t('results.readingNumber'))}
              {sortableHeader('rig_number', t('calibration.rig'))}
              {sortableHeader('well_number', t('calibration.well'))}
              {sortableHeader('sample_source', t('results.sampleSource'))}
              {visibleColumns.has('operator') &&
                sortableHeader('operator', t('results.operator'))}
              {visibleColumns.has('uploaded_at') &&
                sortableHeader('uploaded_at', t('results.uploadedAt'))}
              {sortableHeader('qaqc_status', 'QA/QC')}
              <Table.Th>{t('centrifuge.report')}</Table.Th>
              <Table.Th>{t('common.actions')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && data.length === 0 ? (
              <TableSkeletonRows columns={visibleColumnCount} />
            ) : data.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={visibleColumnCount} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text c="dimmed">{t('results.empty')}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data.map((item) => (
                <Table.Tr
                  key={item.id}
                  className={[
                    selectedRowIdSet.has(item.id) ? classes.selectedRow : '',
                    shouldHighlightCentrifugeRow(item) ? classes.centrifugeCandidateRow : '',
                  ].join(' ')}
                >
                  <Table.Td>
                    <Checkbox
                      checked={selectedRowIdSet.has(item.id)}
                      onChange={() => toggleRowSelection(item)}
                      aria-label={t('results.selectResult', { id: item.id })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" className={classes.dateCell}>
                      {formatDisplayDateTime(item.date_time)}
                    </Text>
                  </Table.Td>
                  {visibleColumns.has('id') && (
                    <Table.Td>
                      <Text fw={600}>{item.id}</Text>
                    </Table.Td>
                  )}
                  {visibleColumns.has('instrument_serial_number') && (
                    <Table.Td>{item.instrument_serial_number || '-'}</Table.Td>
                  )}
                  {visibleColumns.has('reading_number') && (
                    <Table.Td>{item.reading_number ?? '-'}</Table.Td>
                  )}
                  <Table.Td>{item.rig_number || '-'}</Table.Td>
                  <Table.Td>{item.well_number || '-'}</Table.Td>
                  <Table.Td>
                    {sampleSourceDisplayName(
                      sampleSourceCatalog,
                      item.sample_source,
                      language
                    )}
                  </Table.Td>
                  {visibleColumns.has('operator') && (
                    <Table.Td>{item.operator || '-'}</Table.Td>
                  )}
                  {visibleColumns.has('uploaded_at') && (
                    <Table.Td>
                      <Text size="sm" className={classes.dateCell}>
                        {formatDisplayDateTime(item.uploaded_at)}
                      </Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    {item.qaqc_status ? (
                      <Badge
                        variant="light"
                        size="sm"
                        className={getQaqcStatusClass(item.qaqc_status)}
                      >
                        {formatQaqcStatus(item.qaqc_status, t)}
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {item.centrifuge_report_id && item.centrifuge_report_uuid ? (
                      <Button
                        variant="subtle"
                        color="green"
                        size="compact-sm"
                        onClick={() => navigate(
                          `/centrifuge_reports?run_uuid=${item.centrifuge_report_uuid}`
                        )}
                      >
                        {item.centrifuge_report_id}
                      </Button>
                    ) : (
                      <Text size="sm" c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap" className={classes.rowActions}>
                      {!operatorUser && isRerunnableQaqcStatus(
                        item.qaqc_raw_status || item.qaqc_status
                      ) && (
                        <Tooltip label={t('results.rerun')}>
                          <ActionIcon
                            variant="outline"
                            color="orange"
                            size="lg"
                            className={classes.rowAction}
                            aria-label={t('results.rerun')}
                            loading={rerunningIdSet.has(item.id)}
                            disabled={
                              rerunningIds.length > 0 &&
                              !rerunningIdSet.has(item.id)
                            }
                            onClick={() => rerunResults([item.id])}
                          >
                            <IconRefresh size={17} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={t('results.downloadReport')}>
                        <ActionIcon
                          variant="outline"
                          color="green"
                          size="lg"
                          className={classes.rowAction}
                          aria-label={t('results.downloadReport')}
                          onClick={() => downloadPdfReport(item.id)}
                        >
                          <IconDownload size={17} />
                        </ActionIcon>
                      </Tooltip>
                      {!operatorUser && (
                        <Tooltip label={t('results.retortCalibration')}>
                          <ActionIcon
                            variant="outline"
                            color="green"
                            size="lg"
                            className={classes.rowAction}
                            aria-label={t('results.retortCalibration')}
                            onClick={() => navigateToCalibration(
                              item.well_number,
                              item.rig_number
                            )}
                          >
                            <IconSettings size={17} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <div className={classes.paginationRow}>
        <Text size="sm" c="dimmed" className={classes.resultRange}>
          {t('results.showing', {
            from: data.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
            to: Math.min(currentPage * itemsPerPage, totalCount),
            total: totalCount,
          })}
        </Text>
        <Group gap="xs" wrap="nowrap" className={classes.rowsControl}>
            <Text size="xs" fw={700}>
              {t('results.rows')}
            </Text>
            <NativeSelect
              aria-label={t('results.rowsPerPage')}
              data={ITEMS_PER_PAGE_OPTIONS}
              value={String(itemsPerPage)}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (value) {
                  setCurrentPage(1);
                  setItemsPerPage(Number(value));
                }
              }}
              className={classes.rowsSelect}
              size="xs"
            />
        </Group>
        <Pagination
          total={totalPages}
          value={currentPage}
          onChange={setCurrentPage}
          className={classes.pagination}
        />
      </div>

      <GenerateCentrifugeReportModal
        opened={centrifugeModalOpened}
        selection={centrifugeSelection.selection}
        allowReuse={centrifugeSelection.linkedReportIds.length > 0}
        onClose={() => setCentrifugeModalOpened(false)}
        onCreated={(report: CentrifugeReport) => {
          setCentrifugeModalOpened(false);
          clearSelection();
          setRefreshVersion((version) => version + 1);
          navigate(`/centrifuge_reports?run_uuid=${report.run_uuid}`);
        }}
      />

      <Modal
        opened={reuseConfirmationOpened}
        onClose={() => setReuseConfirmationOpened(false)}
        title={t('centrifuge.reuseTitle')}
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {t('centrifuge.selectionReuse', {
              reports: centrifugeSelection.linkedReportIds.join(', '),
            })}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setReuseConfirmationOpened(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="orange"
              onClick={() => {
                setReuseConfirmationOpened(false);
                setCentrifugeModalOpened(true);
              }}
            >
              {t('centrifuge.reuseConfirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
